import { fuelTypes } from "./../../../utils/utilLists";
import {
  execute,
  FilterConditions,
  INSERT,
  SELECT,
  UPDATE,
} from "@/utils/database_module";
import { logDebug } from "@/utils/helpers";
import { Car, SuperCategory, User, UserSession } from "@/utils/types";
import { log } from "console";
import moment from "moment";

const cars = {
  getAll: async (filter: {
    selectedSuperCategoriesIds?: number[];
    selectedOrderColumn?: string;
    selectedSuperCategoriesId?: any;
    priceRange?: number[];
    mileageRange?: number[];
    fuelTypes?: { en?: string; fr?: string; ar?: string }[];
    features?: { en?: string; fr?: string; ar?: string }[];
    transmissionTypes?: { en?: string; fr?: string; ar?: string }[];
    is_new?: string[];
    colors?: { id: number; name: { en: string; fr: string; ar: string } }[];
    limit?: number;
    currentPage?: number;
    searchQuery?: string;
    is_admin?: boolean;
    color?: string;
    startDate?: string;
    endDate?: string;
  }) => {
    let {
      selectedSuperCategoriesIds,
      selectedOrderColumn,
      limit,
      priceRange,
      mileageRange,
      currentPage,
      searchQuery,
      is_admin,
      color,
      colors,
      startDate,
      endDate,
      fuelTypes,
      transmissionTypes,
      features,
      is_new,
    } = filter;

    logDebug("🚗 Cars filter", filter);

    // Define the base table with JOIN on super categories
    const table = `cars p LEFT JOIN super_categories sc ON p.super_category_id = sc.id`;

    // Ensure limit and page have default values
    if (!limit) limit = 100000;
    if (!currentPage) currentPage = 1;

    // === DYNAMIC FILTER STRINGS ===

    // Filter by selected super categories
    const superCategoriesStr = selectedSuperCategoriesIds
      ? `(${selectedSuperCategoriesIds
          .map((superCategoryId) => `p.super_category_id = ${superCategoryId}`)
          .join(" OR ")})`
      : "";

    // Filter by public cars if not admin
    const isPublicStr = is_admin ? "" : "p.is_public = 1";

    // Search by name (case-insensitive)
    const searchQueryStr = searchQuery
      ? `LOWER(p.name) LIKE '%${searchQuery.toLowerCase()}%'`
      : "";

    // Filter by date range
    const carsstartDateStr = startDate
      ? `DATE(p.date) >= '${moment(startDate).format("YYYY-MM-DD")}'`
      : "";
    const carsendDateStr = endDate
      ? `DATE(p.date) <= '${moment(endDate).format("YYYY-MM-DD")}'`
      : "";

    // Filter by transmission (gearbox)
    const transmissionStr =
      transmissionTypes && transmissionTypes.length > 0
        ? `JSON_UNQUOTE(JSON_EXTRACT(p.specifications, '$.gearbox.en')) IN (${transmissionTypes
            .map((t) => `'${t.en}'`)
            .join(",")})`
        : "";

    // Filter by fuel type (energy_type)
    const fuelTypeStr =
      fuelTypes && fuelTypes.length > 0
        ? `JSON_UNQUOTE(JSON_EXTRACT(p.specifications, '$.energy_type.en')) IN (${fuelTypes
            .map((f) => `'${f?.en}'`)
            .join(",")})`
        : "";

    // Filter by colors
    const colorsStr =
      colors && colors.length > 0
        ? `JSON_UNQUOTE(JSON_EXTRACT(p.color, '$.name.en')) IN (${colors
            .map((f) => `'${f?.name.en}'`)
            .join(",")})`
        : "";

    // Filter by new/used status
    const isNewStr =
      is_new && is_new.length > 0 ? `p.is_new IN (${is_new.join(",")})` : "";

    // Filter by features (checks if any selected feature exists in specifications)
    const featuresStr =
      features && features.length > 0
        ? `EXISTS (
        SELECT 1 FROM JSON_TABLE(p.specifications->'$.features', '$[*]' 
          COLUMNS (en VARCHAR(255) PATH '$.en')
        ) AS feat
        WHERE feat.en IN (${features.map((f) => `'${f.en}'`).join(",")})
      )`
        : "";

    // Filter by price range
    const priceRangeStr =
      priceRange && priceRange.length === 2
        ? `p.price BETWEEN ${!isNaN(priceRange[0]) ? priceRange[0] : 0} AND ${
            !isNaN(priceRange[1]) && priceRange[1] > 0
              ? priceRange[1]
              : 9999999999
          }`
        : "";

    // Filter by mileage range
    const mileageRangeStr =
      mileageRange && mileageRange.length === 2
        ? `p.mileage BETWEEN ${
            !isNaN(mileageRange[0]) ? mileageRange[0] : 0
          } AND ${!isNaN(mileageRange[1]) ? mileageRange[1] : 9999999999}`
        : "";

    // === COMBINE FILTERS ===

    // Base filters always applied
    const allFilters = [
      "p.super_category_id IS NOT NULL",
      "p.is_deleted = 0",
      "p.is_primary = 1",
      superCategoriesStr,
      searchQueryStr,
      colorsStr,
      isPublicStr,
      mileageRangeStr,
      priceRangeStr,
      carsstartDateStr,
      carsendDateStr,
      transmissionStr,
      fuelTypeStr,
      isNewStr,
      featuresStr,
    ];

    // Filter out empty strings and invalid values
    const nonEmptyFilters = allFilters.filter(
      (filter) => filter !== "" && filter !== "()"
    );

    // Define sorting rule
    const orderStr =
      selectedOrderColumn && !selectedOrderColumn.includes("name")
        ? `ORDER BY ${selectedOrderColumn}`
        : "ORDER BY date DESC";

    // Calculate total limit (pagination)
    const limitStr = !isNaN(parseInt(`${limit * currentPage}`))
      ? parseInt(`${limit * currentPage}`)
      : limit;

    const columns = "p.*, sc.name as super_category_name";

    // Final SQL query for paginated cars
    logDebug(
      `SELECT ${columns} FROM ${table} WHERE ${nonEmptyFilters.join(
        " AND "
      )} ${orderStr} LIMIT ${limitStr}`
    );

    const cars = (await execute(
      `SELECT ${columns} FROM ${table} WHERE ${nonEmptyFilters.join(
        " AND "
      )} ${orderStr} LIMIT ${limitStr}`
    )) as Car[];

    // Query to get total number of cars (without pagination)
    const allCars = (await execute(
      `SELECT * FROM cars p WHERE ${nonEmptyFilters.join(" AND ")}`
    )) as Car[];

    logDebug(cars);

    // Return paginated cars and total count
    return {
      cars: cars,
      total: allCars.length,
    };
  },

  getOne: async (id: number, session: UserSession, isCarPage: boolean) => {
    // Define the base table with LEFT JOIN to get super category name
    const table = `cars p  LEFT JOIN super_categories sc ON p.super_category_id = sc.id`;

    // Fetch the car by ID, ensuring it's not deleted
    const [car] = (await SELECT(table, {
      conditions: [
        { column: "p.id", operator: "=", value: id },
        { column: "p.is_deleted", operator: "=", value: 0 },
      ],
      columns: ["p.*", "sc.name as super_category_name"],
    })) as Car[];
    // If no car is found, throw an error
    if (!car) throw new Error("Car not found");

    // Fetch other variants of the same reference (excluding this car)
    const variants = (await SELECT(`cars`, {
      columns: ["id", "reference", "images", "color", "name"],
      conditions: [
        { column: "reference", operator: "=", value: car.reference! },
        { column: "id", operator: "<>", value: car.id! },
        { column: "is_deleted", operator: "=", value: 0 },
      ],
    })) as Car[];


    // If on the car page, append images from other variants to the current car
    if (isCarPage) {
      for (const variant of variants) {
        if (variant.id !== car.id) {
          for (const image of variant.images) {
            car.images.push(image);
          }
        }
      }
    }
    // Group all car variants (including this car) by color
    car.colors = [...variants, car];

    // Attach variants array separately
    car.variants = variants;

    // If the session user is an admin, retrieve and attach user info
    if (
      (session?.role === "admin" || session?.role === "super-admin") &&
      car.adder_id
    ) {
      const users = (await SELECT("users", {
        conditions: [
          { column: "id", operator: "=", value: parseInt(`${car.adder_id}`) },
        ],
      })) as User[];
      if (users.length === 0) return car;
      const user = users[0];
      if (!user) throw new Error("User not found");
      // Parse phone numbers from string to array
      user.phone_numbers =
        typeof user?.phone_numbers === "string"
          ? JSON.parse(`${user?.phone_numbers}`)
          : user?.phone_numbers;

      // Attach user object to car
      car.user = user;
    }

    // Return the full car object with user and variants info
    return car;
  },

  insert: async (car: SharedFields & SpecificFields) => {
    if (!car.reference) car.reference = generateRandomCarReference();
    const addedProduct = (await INSERT("cars", car)) as Car;
    return addedProduct;
  },

  update: async (id: number, car: ProductToUpdate) => {
    const variantsSharedFileds = car.sharedFields;
    await UPDATE("cars", variantsSharedFileds, [
      { column: "reference", operator: "=", value: car.actualReference },
    ]);

    const carSpecificFileds = car.specificFields;
    if (carSpecificFileds)
      await UPDATE("cars", carSpecificFileds, [
        { column: "id", operator: "=", value: id },
      ]);
    return true;
  },

  delete: async (id: number) => {
    await UPDATE("cars", { is_deleted: 1 }, [
      { column: "id", operator: "=", value: id },
    ]);
  },

  recommend: async (id: number) => {
    const [car] = (await SELECT("cars", {
      conditions: [{ column: "id", operator: "=", value: id }],
    })) as Car[];
    const similarCars = (await execute(
      `SELECT cars.* FROM cars WHERE ( super_category_id = ${car?.super_category_id}) AND  cars.id <> ${id}   AND cars.is_deleted =0 LIMIT 10`
    )) as Car[];

    return similarCars;
  },

  getBrands: async () => {
    const brands = (await SELECT("super_categories", {
      conditions: [
        { column: "is_deleted", operator: "=", value: 0 },
        { column: "is_cars", operator: "=", value: 1 },
      ] as FilterConditions,
      order: "display_order ASC",
    })) as SuperCategory[];
    return brands;
  },
};

export default cars;

function generateRandomCarReference(length = 5) {
  const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  let reference = "";
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length);
    reference += characters[randomIndex];
  }
  return reference;
}

type CarToUpdate = {
  id?: number;
  name?: string;
  reference?: string;
  color?: string;
  images?: string[];
  super_category_id?: number;
  is_public?: number;
  is_deleted?: number;
  date?: string;
  modified_date?: string;
  views?: number;
};

type ProductToUpdate = {
  sharedFields: SharedFields;
  specificFields?: SpecificFields;
  actualReference: string;
  oldShippingCost?: number;
  oldPurchasePrice?: number;
  user?: UserSession;
};

type SharedFields = {
  super_category_id?: number | null;
  description?: { en?: string; fr?: string; ar?: string };
  name?: { en?: string; fr?: string; ar?: string };
  reference?: string;
  tags?: string[];
  views?: number;
  is_public?: 0 | 1;
  adder_id?: number;
  price?: number;
};

type SpecificFields = {
  color?: string;
  rating?: number;
  images: string;
  is_deleted?: 0 | 1;
};
